import * as React from 'react';
import { Autocomplete, Box, TextField, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SuccessFailureIndicator } from '../../../subs/SuccessFailureIndicator';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { WorkshopResult, WorkshopTestResultStrings, getWorkshopResultsForWP } from '../../../types/WorkshopResult';
import { WorkshopProduct, getWorkshopProducts } from '../../../types/WorkshopProducts';
import { Workshop, getWorkshops } from '../../../types/Workshop';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const WorkshopResultsAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [workshopproducts, setWorkshopProducts] = React.useState<WorkshopProduct[]>([]);
    const [workshopproduct, setWorkshopProduct] = React.useState<WorkshopProduct>();
    const [allData, setallData] = React.useState<WorkshopResult[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllWorkshops = async () => {
            const allworkshopsResponse = await getWorkshops(new AbortController());
            if (allworkshopsResponse.failed) {
                showUserFeedback(allworkshopsResponse.errorText);
                return;
            }
            setWorkshops(allworkshopsResponse.result);
        }
        fetchAllWorkshops();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProducts = async () => {
            if (workshop === undefined || workshop.id === 0) return;
            const allWPResponse = await getWorkshopProducts(workshop.id, new AbortController());
            if (allWPResponse.failed) {
                showUserFeedback(allWPResponse.errorText);
                return;
            }
            setWorkshopProducts(allWPResponse.result);
        }
        fetchAllWorkshopProducts();
    }, [workshop, showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProductProperties = async () => {
            if (workshopproduct === undefined || workshopproduct.id === 0) return;
            const allWPPResponse = await getWorkshopResultsForWP(workshopproduct.id, new AbortController());
            if (allWPPResponse.failed) {
                showUserFeedback(allWPPResponse.errorText);
                return;
            }
            setallData(allWPPResponse.result);
        }
        fetchAllWorkshopProductProperties();
    }, [workshopproduct, showUserFeedback]);

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'interfaceId',
            headerName: 'Interface ID',
            flex: 1,
        },
        {
            field: 'workshopId',
            headerName: 'Workshop ID',
            flex: 1,
        },
        {
            field: 'clientId',
            headerName: 'Client ID',
            flex: 1,
        },
        {
            field: 'serverId',
            headerName: 'Server ID',
            flex: 1,
        },
        {
            field: 'testCombinationId',
            headerName: 'Test Combination ID',
            flex: 1,
        },
        {
            field: 'counterCalcId',
            headerName: 'Counter Calc ID',
            flex: 1,
        },
        {
            field: 'testsPassed',
            headerName: 'Tests Passed',
            flex: 1,
        },
        {
            field: 'testsFailed',
            headerName: 'Tests Failed',
            flex: 1,
        },
        {
            field: 'testsWarning',
            headerName: 'Tests Warning',
            flex: 1,
        },
        {
            field: 'testsNotCompleted',
            headerName: 'Tests Not Completed',
            flex: 1,
        },
        {
            field: 'clientNotSupported',
            headerName: 'Client Not Supported',
            flex: 1,
        },
        {
            field: 'serverNotSupported',
            headerName: 'Server Not Supported',
            flex: 1,
        },
        {
            field: 'testsNotCompleted',
            headerName: 'Tests Not Completed',
            flex: 1,
        },
        {
            field: 'testResult',
            headerName: 'Test Result',
            flex: 1,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return WorkshopTestResultStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return WorkshopTestResultStrings[value];
            },
            valueSetter: (value, row) => {
                let testResult = 0;
                if (WorkshopTestResultStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < WorkshopTestResultStrings.length; i++) {
                        if (WorkshopTestResultStrings[i] === value) {
                            testResult = i;
                            break;
                        }
                    }
                }
                return { ...row, testResult: testResult };
            },
            type: 'singleSelect',
            valueOptions: WorkshopTestResultStrings
        },
        {
            field: 'clientAccepted',
            headerName: 'Client Accepted',
            type: 'boolean',
            flex: 1,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.clientAccepted} /> },
        },
        {
            field: 'serverAccepted',
            headerName: 'Server Accepted',
            type: 'boolean',
            flex: 1,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.serverAccepted} /> },
        },
        {
            field: 'started',
            headerName: 'Started',
            type: 'boolean',
            flex: 1,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.started} /> },
        },
        {
            field: 'comment',
            headerName: 'Comment',
            flex: 1,
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsOptions' options={workshops.map(w => w.name)}
                    value={workshop ? workshop.name : ''}
                    onChange={(e, value) => setWorkshop(workshops.find(w => w.name === value))}
                    renderInput={(params) =>
                        <TextField name='wsOptions' multiline
                            {...params}
                            label={"Workshop"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        {
            workshop &&
            <Grid size={12} paddingBottom={10}>
                <FormControl required fullWidth>
                    <Autocomplete id='ac-wspOptions' options={workshopproducts.map(w => w.guid)}
                        value={workshopproduct ? workshopproduct.guid : ''}
                        onChange={(e, value) => setWorkshopProduct(workshopproducts.find(w => w.guid === value))}
                        renderInput={(params) =>
                            <TextField name='wspOptions' multiline
                                {...params}
                                label={"Workshop Product"}
                                sx={{
                                    '& .MuiOutlinedInput-root fieldset': {
                                        borderColor: 'divider',
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                        borderColor: 'divider',
                                    },
                                }}
                            />
                        }
                    />
                </FormControl>
            </Grid>
        }
        {
            workshopproduct &&
            <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
                <EditableTable
                    columns={columns}
                    rows={allData}
                    setAllRows={setallData}
                />
            </Box>
        }
    </React.Fragment >
}